import { useAtom, useSetAtom } from 'jotai';
import { cloneDeep, merge } from 'lodash-es';
import { useCallback } from 'react';
import {
  ICustomNumberFormatConfig,
  IFormatterDataModel,
  StandardFormatterId,
} from '@foresightdata/web-common-core';
import { FMT } from '@foresightdata/web-common-formatter-service';
import { defaultCurrency } from '../services/state/AppConfigStateJ';
import { currentCurrencyCodeAtom, selectedCurrencyPrefAtom } from '../services/state/CurrencyStateJ';
import { CurrencyCode } from '../util/currency-util';

/**
 * Custom hook to handle the selected currency
 */
export function useSelectedCurrency() {
  // App level preference
  const setSelectedCurrencyPref = useSetAtom(selectedCurrencyPrefAtom);
  // Local state needed for now as FX is only supported in portco views.
  const [selectedCurrency, _setSelectedCurrency] = useAtom(currentCurrencyCodeAtom);

  const setSelectedCurrency = useCallback(
    (currency?: CurrencyCode, updatePref = true) => {
      const newCurrency = !currency ? defaultCurrency : currency;

      updateCurrencyFormatters(newCurrency);
      _setSelectedCurrency(newCurrency);

      if (updatePref) {
        setSelectedCurrencyPref({ currency: newCurrency });
      }
    },
    [_setSelectedCurrency, setSelectedCurrencyPref]
  );

  return {
    selectedCurrency,
    setSelectedCurrency,
  };
}

function updateCurrencyFormatters(newCurrency: CurrencyCode) {
  const allFormatters = FMT.get().getAllFormatterIds();
  const newFormat: Partial<IFormatterDataModel<ICustomNumberFormatConfig>> = {
    config: { currency: newCurrency },
  };

  allFormatters.forEach((formatterId) => {
    // USD formatter are legacy
    if (formatterId.startsWith('currency') || formatterId.startsWith('usd')) {
      const currentModel = FMT.get().getFormatterModel(
        formatterId as StandardFormatterId
      )! as IFormatterDataModel<ICustomNumberFormatConfig>;
      const newModel = merge(cloneDeep(currentModel), newFormat);

      FMT.get().setFormatterFromConfigForId(formatterId, newModel);
    }
  });
}
